import React from 'react'
import {
  LuCalendar1,
  LuCircleCheck,
  LuClock,
  LuHouse,
  LuMapPin,
  LuUser,
} from 'react-icons/lu'

import { MEETING_FORMAT_DETAILS } from '@/components/integration/nylas/scheduler/constants'
import { formatClientHomeAddress } from '@/components/integration/nylas/scheduler/helpers'
import { BookedMeetingDetails } from '@/components/integration/nylas/scheduler/types'
import { Heading } from '@/components/ui/heading'
import { DATE_FORMATS, MEETING_FORMAT } from '@/lib/constants'
import { formatDate, getRelativeDayLabel } from '@/lib/helpers/dateHelpers'
import { formatLocationAddress } from '@/lib/helpers/helperFunctions'

interface Props {
  bookingDetails: BookedMeetingDetails
  toCompanyName: string
}

const ConfirmedBookingDetails: React.FC<Props> = ({
  bookingDetails,
  toCompanyName,
}) => {
  // Example format: Thursday, 26 December 2025
  const date = formatDate(bookingDetails.end, 'EEEE, d MMMM yyyy')
  // e.g. "Tomorrow" / "This Thursday" — null when further out than this week.
  const relativeDay = getRelativeDayLabel(bookingDetails.end)

  // `start`/`end` are typed as Date but arrive as ISO strings from the API, so
  // coerce before doing arithmetic.
  const durationMinutes = Math.round(
    (new Date(bookingDetails.end).getTime() -
      new Date(bookingDetails.start).getTime()) /
      60000,
  )

  const formatDetails = bookingDetails.format
    ? MEETING_FORMAT_DETAILS[bookingDetails.format]
    : null
  const FormatIcon = formatDetails?.icon

  // For a home visit the location is the office handling it, not where the
  // meeting is, so it's shown under the address rather than as the venue
  const isAtClientHome =
    bookingDetails.format === MEETING_FORMAT.in_person_client_home
  const clientHomeAddress = isAtClientHome
    ? formatClientHomeAddress({
        addressLine1: bookingDetails.clientAddressLine1,
        townOrCity: bookingDetails.clientTownOrCity,
        postcode: bookingDetails.clientPostcode,
      })
    : ''

  function showTimeZone() {
    return (
      !!bookingDetails.timezone &&
      // Don't bother for UK time
      bookingDetails.timezone !== 'Europe/London'
    )
  }

  return (
    <>
      <header className="mb-4 text-center">
        <div
          className="mb-4 flex flex-col items-center justify-center"
          style={{ gap: '10px' }}
        >
          <div className="p-6 bg-green-100 rounded-full pop-in text-success">
            <LuCircleCheck size={64} />
          </div>

          <Heading
            size={2}
            className="mb-0 font-bold"
            style={{ fontSize: '26px' }}
          >
            Meeting confirmed
          </Heading>
        </div>

        {/* Not saying who it's been sent to here, because it
          could be client or toCompany doing the booking */}
        <p className="text-balance text-md mb-0">
          A calendar invitation has been sent for your meeting with{' '}
          <strong>{bookingDetails.bookedWithFullName}</strong> of{' '}
          <strong>{toCompanyName}</strong>.
        </p>
      </header>

      <section className="rounded-3xl shadow-lg border p-6">
        <Heading size={4} className="mb-4 text-black font-bold">
          Meeting details
        </Heading>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <IconBubble>
              <LuUser size={16} />
            </IconBubble>

            <div>
              {bookingDetails.participants.map((participant, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-muted-foreground">, </span>}
                  <span>{participant.name}</span>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <IconBubble>
              <LuUser size={16} />
            </IconBubble>

            {bookingDetails.bookedWithFullName}
          </div>

          {formatDetails && FormatIcon && (
            <div className="flex items-center gap-3">
              <IconBubble>
                <FormatIcon size={16} />
              </IconBubble>

              {formatDetails.label}
            </div>
          )}

          {isAtClientHome && clientHomeAddress && (
            <div className="flex items-center gap-3">
              <IconBubble>
                <LuHouse size={16} />
              </IconBubble>

              <div className="flex flex-col leading-4">
                Home visit
                <span className="text-muted-foreground font-light">
                  {clientHomeAddress}
                </span>
                {bookingDetails.location && (
                  <span className="text-muted-foreground mt-1 font-light">
                    Handled by {bookingDetails.location.name}
                  </span>
                )}
              </div>
            </div>
          )}

          {!isAtClientHome && bookingDetails.location && (
            <div className="flex items-center gap-3">
              <IconBubble>
                <LuMapPin size={16} />
              </IconBubble>

              <div className="flex flex-col leading-4">
                {bookingDetails.location.name}
                <span className="text-muted-foreground font-light">
                  {formatLocationAddress(bookingDetails.location)}
                </span>
                {bookingDetails.location.clientNotes && (
                  <span
                    className="text-muted-foreground mt-1 font-light
                      whitespace-pre-line"
                  >
                    {bookingDetails.location.clientNotes}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <IconBubble>
              <LuCalendar1 size={16} />
            </IconBubble>

            <div className="flex flex-col leading-4">
              {relativeDay ? (
                <>
                  {relativeDay}
                  <span className="text-muted-foreground font-light">
                    {date}
                  </span>
                </>
              ) : (
                date
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <IconBubble>
              <LuClock size={16} />
            </IconBubble>

            <div className="flex flex-col leading-4">
              {formatDate(bookingDetails.start, DATE_FORMATS.TIME, {
                timeZone: bookingDetails.timezone,
              })}{' '}
              {showTimeZone() ? (
                <span className="text-muted-foreground font-light">{` (${bookingDetails.timezone})`}</span>
              ) : null}
              <span className="text-muted-foreground font-light">
                {durationMinutes} minutes
              </span>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

const IconBubble = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      className="size-10 bg-gray-100 rounded-xl grid place-items-center border"
    >
      {children}
    </div>
  )
}

export default ConfirmedBookingDetails
